import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { MailProcessing, MailProcessingEvaluation } from '../db/MailExtractionResult.js';
import { MailProcessingFilter } from '../db/MailProcessingFilter.js';
import { MailProcessingUserStats } from '../db/MailProcessingUserStats.js';
import { senderLookupStages } from '../server/stats-sender-pipeline.js';

interface UserStatsRow {
  _id: string;
  userName?: string;
  sumExtractions: number;
  sumCorrect: number;
  sumIncorrect: number;
  sumNotEvaluated: number;
  sumCorrectAfterCorrection: number;
  sumCorrectWithAdjustment: number;
  sumUnsolvable: number;
}

/**
 * Per-user extraction statistics, grouped by sender address and joined back to
 * the known users. Honours the same month/evaluation filter as the global
 * stats, but ignores its sender scope (this query *is* the per-user breakdown).
 */
export const getMailProcessingUserStats = registerQuery({
  type: () => [MailProcessingUserStats],
  operationName: 'getMailProcessingUserStats',
  resolve: (filter?: MailProcessingFilter) => {
    return async (gqlFields: BaseQLSelectionSet<MailProcessingUserStats>) => {
      const mailProcessingCollection = await getRawCollection<MailProcessing>(MailProcessing._collectionName);

      const rows = await mailProcessingCollection
        .aggregate<UserStatsRow>([
          ...senderLookupStages(),
          { $match: filter?.getMongoMatchFilter() ?? {} },
          { $match: { _senderAddress: { $nin: ['', null] } } },
          {
            $group: {
              _id: '$_senderAddress',
              sumExtractions: { $sum: 1 },
              sumCorrect: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT] }, 1, 0] },
              },
              sumIncorrect: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.FALSE] }, 1, 0] },
              },
              sumNotEvaluated: {
                $sum: { $cond: [{ $eq: ['$evaluation', null] }, 1, 0] },
              },
              sumCorrectAfterCorrection: {
                $sum: {
                  $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT_AFTER_CORRECTION] }, 1, 0],
                },
              },
              sumCorrectWithAdjustment: {
                $sum: {
                  $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT] }, 1, 0],
                },
              },
              sumUnsolvable: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.UNSOLVABLE_INQUIRY] }, 1, 0] },
              },
            },
          },
          // attribute only to known users (sender e-mail matches a user account)
          {
            $lookup: {
              from: AdornisUser._collectionName,
              let: { addr: '$_id' },
              pipeline: [
                { $match: { $expr: { $eq: [{ $toLower: '$email' }, '$$addr'] } } },
                { $project: { username: 1 } },
              ],
              as: '_user',
            },
          },
          { $match: { _user: { $ne: [] } } },
          { $addFields: { userName: { $ifNull: [{ $arrayElemAt: ['$_user.username', 0] }, '$_id'] } } },
          { $sort: { sumExtractions: -1 } },
        ])
        .toArray();

      return rows.map(
        row =>
          new MailProcessingUserStats({
            senderAddress: row._id,
            userName: row.userName ?? row._id,
            sumExtractions: row.sumExtractions,
            sumCorrect: row.sumCorrect,
            sumIncorrect: row.sumIncorrect,
            sumNotEvaluated: row.sumNotEvaluated,
            sumCorrectAfterCorrection: row.sumCorrectAfterCorrection,
            sumCorrectWithAdjustment: row.sumCorrectWithAdjustment,
            sumUnsolvable: row.sumUnsolvable,
          }),
      );
    };
  },
  params: [{ type: () => MailProcessingFilter, name: 'filter' }],
});
